import axios from 'axios';

// Voor productie: REACT_APP_API_URL=https://api.demo-grondwaterregister.nl/api
// Voor lokale dev: niet zetten, default is localhost:8002.
const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8002/api';

// App-level auth: de sessiecookie (HttpOnly) gaat mee dankzij withCredentials.
// De vroegere hardgecodeerde Basic Auth is verwijderd nu nginx geen basic-auth
// meer op /api afdwingt — bescherming loopt volledig via de sessie.
const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
});

// Publieke endpoints zitten op /publiek/... (geen /api-prefix, geen auth).
// Tweede axios-instance met baseURL zonder /api zodat we vanuit dezelfde host roepen.
const publiekBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
const publiek = axios.create({
  baseURL: publiekBaseUrl,
});

// Auth (app-level: sessie + HttpOnly-cookie)
export const inloggen = (email, wachtwoord) => api.post('/auth/login', { email, wachtwoord });
export const uitloggen = () => api.post('/auth/logout');
export const getIngelogdeGebruiker = () => api.get('/auth/me');

// Passkeys (WebAuthn)
export const passkeyRegistratieStart = () => api.post('/passkeys/registratie/start');
export const passkeyRegistratieVoltooi = (credential, apparaatnaam) =>
  api.post('/passkeys/registratie/voltooi', { credential, apparaatnaam });
export const passkeyLoginStart = () => api.post('/passkeys/login/start');
export const passkeyLoginVoltooi = (credential) =>
  api.post('/passkeys/login/voltooi', { credential });
export const getPasskeys = () => api.get('/passkeys');
export const verwijderPasskey = (id) => api.delete(`/passkeys/${id}`);

// Profiel & wachtwoord (eigen account)
export const opslaanProfiel = (voornaam, tussenvoegsel, achternaam) =>
  api.post('/auth/profiel', { voornaam, tussenvoegsel, achternaam });
export const wijzigWachtwoord = (huidig_wachtwoord, nieuw_wachtwoord) =>
  api.post('/auth/wachtwoord-wijzigen', { huidig_wachtwoord, nieuw_wachtwoord });

// Gebruikersbeheer (alleen beheerder)
export const getGebruikers = () => api.get('/beheer/gebruikers');
export const maakGebruiker = (data) => api.post('/beheer/gebruikers', data);
export const wijzigGebruiker = (id, data) => api.patch(`/beheer/gebruikers/${id}`, data);
export const resetGebruikerWachtwoord = (id, nieuw_wachtwoord) =>
  api.post(`/beheer/gebruikers/${id}/wachtwoord-reset`, { nieuw_wachtwoord });

// Houders
export const getHouders = () => api.get('/houders');
export const zoekHouders = (params) => api.get('/houders/zoek', { params });
export const getHouder = (id) => api.get(`/houders/${id}`);
export const getHouderDetail = (id) => api.get(`/houders/${id}/detail`);
export const maakHouder = (data) => api.post('/houders', data);
export const wijzigHouder = (id, data) => api.put(`/houders/${id}`, data);
export const verwijderHouder = (id) => api.delete(`/houders/${id}`);

// Inrichtingen
export const getInrichtingen = (houder_id) => api.get('/inrichtingen', { params: { houder_id } });
export const getInrichtingDetail = (id) => api.get(`/inrichtingen/${id}/detail`);
export const getInrichtingHistorie = (id) => api.get(`/inrichtingen/${id}/historie`);
export const maakInrichting = (data) => api.post('/inrichtingen', data);
export const maakInrichtingHandmatig = (data) => api.post('/inrichtingen-handmatig', data);
export const wijzigInrichting = (id, data) => api.put(`/inrichtingen/${id}`, data);
export const beeindigInrichting = (id, einddatum) =>
  api.post(`/inrichtingen/${id}/beeindigen`, null, { params: { einddatum } });
export const overdragenInrichting = (id, nieuwe_houder_id, overdrachtsdatum) =>
  api.post(`/inrichtingen/${id}/overdragen`, null, {
    params: { nieuwe_houder_id, overdrachtsdatum }
  });

// Putten
export const getPutten = (inrichting_id) => api.get('/putten', { params: { inrichting_id } });
/* Zoekt in het migratieblok van de aantekening (alleen de bijdrage van
   Datamigratie) — zo vind je een put terug op een oud WBD- of kaartnummer. */
export const zoekPutOpmerking = (q) => api.get('/putten/zoek-opmerking', { params: { q } });
export const getPutDetail = (id) => api.get(`/putten/${id}/detail`);
export const maakPut = (data) => api.post('/putten', data);
export const wijzigPut = (id, data) => api.put(`/putten/${id}`, data);
export const buitenGebruikPut = (id, datum) =>
  api.post(`/putten/${id}/buiten-gebruik`, null, { params: { datum } });
export const verstuurVeldwerkLink = (id, inrichting_id) =>
  api.post(`/putten/${id}/veldwerk-link`, { inrichting_id });

export const verstuurAfdichtingsverzoek = (id) =>
  api.post(`/putten/${id}/afdichtingsverzoek`);

export const startAfdichtingsprocedure = (id, herkomst) =>
  api.post(`/putten/${id}/afdichtingsprocedure`, { herkomst });
export const stopAfdichtingsprocedure = (id) =>
  api.delete(`/putten/${id}/afdichtingsprocedure`);

// Koppelingen
export const koppelPut = (data) => api.post('/inrichting-put', data);
export const ontkoppelPut = (inrichting_id, put_id, eind_datum) =>
  api.delete(`/inrichting-put/${inrichting_id}/${put_id}`, { params: { eind_datum } });

// Import
export const importMeldingPreview = (formData) =>
  api.post('/import-melding/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const importMelding = (data) => api.post('/import-melding', data);
/* Alleen de putten uit een meldingsformulier-PDF, met hun registermatch. Voor
   het scherm "Nieuwe put": daar hoort geen melding en geen inrichting bij, dus
   ook niet de kop van het meldingsformulier. Leest; schrijft niets. */
export const puttenUitPdf = (formData) =>
  api.post('/putten/uit-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

// Bulk
export const bulkImportPreview = (formData) =>
  api.post('/bulk-import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const bulkImport = (formData) =>
  api.post('/bulk-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

// Gebieden
// In-memory cache voor /gebieden (en vooral ?met_geometrie=true, dat is ~144MB).
// Cache survives navigatie tussen pagina's binnen dezelfde tab. Wordt vernieuwd
// na TTL of bij tab-reload (gebieden veranderen alleen 's nachts via DSO-sync,
// dus 5 minuten TTL is meer dan veilig). Pending Promise wordt herbruikt zodat
// parallelle callers niet beide een fetch starten (zelfde anti-pattern als
// loadDsoFeatures/getBeschermdGebieden in het Meldingsformulier).
const _gebiedenCache = new Map();  // key: "type|metGeometrie" -> {data, ts, pending}
const GEBIEDEN_TTL_MS = 5 * 60 * 1000;

export const getGebieden = (type, metGeometrie = false) => {
  const key = `${type || ''}|${metGeometrie}`;
  const now = Date.now();
  const entry = _gebiedenCache.get(key);

  if (entry && entry.data && (now - entry.ts) < GEBIEDEN_TTL_MS) {
    // Cache-hit: returnt resolved Promise met cached data
    return Promise.resolve({ data: entry.data });
  }
  if (entry && entry.pending) {
    // Pending fetch: alle callers wachten op dezelfde Promise
    return entry.pending;
  }

  const pending = api.get('/gebieden', {
    params: { ...(type ? { type } : {}), ...(metGeometrie ? { met_geometrie: true } : {}) },
  }).then(resp => {
    _gebiedenCache.set(key, { data: resp.data, ts: Date.now(), pending: null });
    return resp;
  }).catch(err => {
    // Bij fout: cache niet seten, volgende caller probeert opnieuw
    _gebiedenCache.delete(key);
    throw err;
  });

  _gebiedenCache.set(key, { data: null, ts: 0, pending });
  return pending;
};
export const getGebied = (id) => api.get(`/gebieden/${id}`);
export const getPutGebiedsstatus = (id) => api.get(`/putten/${id}/gebiedsstatus`);

// Gebied-styling — kleur/patroon per type, bewerkt via Beheertools.
// In-memory cache met 24u TTL (zelfde patroon als getGebieden) zodat
// elke kaart de huidige stijl direct beschikbaar heeft zonder netwerk-hit.
let _stylingCache = null;       // { data: [...], ts: number, pending: Promise }
const STYLING_CACHE_MS = 24 * 60 * 60 * 1000;

export const getStyling = () => {
  const nu = Date.now();
  if (_stylingCache && _stylingCache.data && (nu - _stylingCache.ts) < STYLING_CACHE_MS) {
    return Promise.resolve({ data: _stylingCache.data });
  }
  if (_stylingCache && _stylingCache.pending) {
    return _stylingCache.pending;
  }
  const pending = api.get('/styling/gebieden')
    .then(res => {
      _stylingCache = { data: res.data, ts: Date.now(), pending: null };
      return res;
    })
    .catch(err => {
      _stylingCache = null;
      throw err;
    });
  _stylingCache = { data: null, ts: 0, pending };
  return pending;
};

// Synchrone helper: hoogstens-één-render-vertraging na de preload-fetch.
// Geeft de huidige cache-data of null als nog niet geladen.
export const getStylingSync = () => (_stylingCache && _stylingCache.data) || null;

// Cache invalideren — bv. na opslaan in Beheertools.
export const invalideerStylingCache = () => { _stylingCache = null; };

export const updateStyling = async (type, payload) => {
  const res = await api.put(`/styling/gebieden/${type}`, payload);
  invalideerStylingCache();  // volgende getStyling-call haalt verse data
  return res;
};

// Grondwaterproductie
export const getGrondwaterproductie = (inrichtingId) =>
  api.get(`/inrichtingen/${inrichtingId}/grondwaterproductie`);
export const createGrondwaterproductie = (inrichtingId, data) =>
  api.post(`/inrichtingen/${inrichtingId}/grondwaterproductie`, data);
export const updateGrondwaterproductie = (metingId, data) =>
  api.put(`/grondwaterproductie/${metingId}`, data);
export const deleteGrondwaterproductie = (metingId) =>
  api.delete(`/grondwaterproductie/${metingId}`);

// Productieopgave opvragen bij de houder (login-loze flow)
export const getProductieStatus = (inrichtingId, jaar) =>
  api.get(`/inrichtingen/${inrichtingId}/productie-status`, { params: jaar ? { jaar } : {} });
export const verstuurProductieLink = (inrichtingId, jaar) =>
  api.post(`/inrichtingen/${inrichtingId}/productie-link`, { jaar });
export const verstuurProductieUitnodigingenBulk = (jaar) =>
  api.post(`/beheer/productie-uitnodigingen`, { jaar });
export const getProductieOverzicht = (jaar) =>
  api.get(`/beheer/productie-overzicht`, { params: jaar ? { jaar } : {} });

// Presence (awareness op detailpagina's)
export const presenceHeartbeat = (type, id, sessieId, editing) =>
  api.post(`/presence/heartbeat`, { type, id, sessie_id: sessieId, editing });
export const presenceAfmelden = (type, id, sessieId, editing) =>
  api.post(`/presence/afmelden`, { type, id, sessie_id: sessieId, editing });

// Perceel-lookup via PDOK
export const lookupPerceel = (x, y) =>
  api.get('/perceel/lookup', { params: { x, y } });


export const getKeurkaartCheck = (x, y, bijlage) =>
  publiek.get('/publiek/keurkaart/check', { params: { x, y, bijlage } });

// In-memory cache voor /publiek/deelgebieden.geojson (~541 KB).
// Zelfde patroon als _gebiedenCache: 5min TTL, pending-Promise herbruik.
let _deelgebiedenCache = null;
let _deelgebiedenCacheTs = 0;
let _deelgebiedenPending = null;
const DEELGEBIEDEN_TTL_MS = 5 * 60 * 1000;

export const getDeelgebieden = () => {
  const now = Date.now();
  if (_deelgebiedenCache && (now - _deelgebiedenCacheTs) < DEELGEBIEDEN_TTL_MS) {
    return Promise.resolve({ data: _deelgebiedenCache });
  }
  if (_deelgebiedenPending) {
    return _deelgebiedenPending;
  }
  _deelgebiedenPending = publiek.get('/publiek/deelgebieden.geojson').then(resp => {
    _deelgebiedenCache = resp.data;
    _deelgebiedenCacheTs = Date.now();
    _deelgebiedenPending = null;
    return resp;
  }).catch(err => {
    _deelgebiedenPending = null;
    throw err;
  });
  return _deelgebiedenPending;
};

export default api;
export async function verstuurBijwerkingsmail(inrichting_id) {
  const r = await api.post(`/inrichtingen/${inrichting_id}/email-bijwerking`);
  return r.data;
}
export async function getEmailBijwerkingStatus(inrichting_id) {
  const r = await api.get(`/inrichtingen/${inrichting_id}/email-bijwerking/status`);
  return r.data;
}

export async function updateHouderOpmerkingen(houder_id, opmerkingen) {
  const r = await api.patch(`/houders/${houder_id}/opmerkingen`, { opmerkingen });
  return r.data;
}

export async function updateInrichtingOpmerkingen(inrichting_id, opmerkingen) {
  const r = await api.patch(`/inrichtingen/${inrichting_id}/opmerkingen`, { opmerkingen });
  return r.data;
}

export async function updatePutOpmerkingen(put_id, opmerkingen) {
  const r = await api.patch(`/putten/${put_id}/opmerkingen`, { opmerkingen });
  return r.data;
}

export const verwerkWijzigingsbesluit = (inrichting_id, body) =>
  api.post(`/inrichtingen/${inrichting_id}/wijzigingsbesluit`, body);

export const activeerGeplande = () =>
  api.post('/activeer-geplande');

// Er kunnen er meerdere klaarstaan, dus zeggen we welke. Zonder gepland_id
// weigert de backend zodra het er meer dan één zijn.
export const annuleerGeplandeWijziging = (inrichting_id, gepland_id) =>
  api.post(`/inrichtingen/${inrichting_id}/wijzigingsbesluit/annuleer-gepland`, null,
    gepland_id ? { params: { gepland_id } } : undefined);

// Klaarstaande meldingen: verwerkt, maar nog niet ingegaan. De payload komt
// mee zodat het verwerkscherm de melding kan terugladen zoals hij binnenkwam.
export const getGeplandeMelding = (gepland_id) =>
  api.get(`/geplande-meldingen/${gepland_id}`);
export const wijzigGeplandeMelding = (gepland_id, body) =>
  api.put(`/geplande-meldingen/${gepland_id}`, body);
export const trekGeplandeMeldingIn = (gepland_id) =>
  api.delete(`/geplande-meldingen/${gepland_id}`);

// Veldwerk in bulk: één link voor alle putten van een inrichting waarvan nog
// gegevens ontbreken, in plaats van een aparte mail per put.
export const getVeldwerkBulkStatus = (id) =>
  api.get(`/inrichtingen/${id}/veldwerk-bulk-status`);
export const verstuurVeldwerkBulkLink = (id) =>
  api.post(`/inrichtingen/${id}/veldwerk-bulk-link`);

// De publieke inrichtingspagina: de link die de melder krijgt en de mailtekst
// die de behandelaar erbij plakt. Vernieuwen maakt de oude link ongeldig.
export const getPubliekeLink = (id) =>
  api.get(`/inrichtingen/${id}/publieke-link`);
export const vernieuwPubliekeLink = (id) =>
  api.post(`/inrichtingen/${id}/publieke-link/vernieuwen`);

// Melding-document (PDF). Het document wordt niet hier gebouwd maar door de
// generator van het Meldingsformulier; het register vraagt eerst of het kan
// (en anders waarom niet) en daarna een eenmalig token plus de URL waarop die
// generator te vinden is. Zie MeldingDocumentModal.
export const getMeldingDocumentGegevens = (id) =>
  api.get(`/inrichtingen/${id}/melding-document`);
export const maakMeldingDocumentToken = (id) =>
  api.post(`/inrichtingen/${id}/melding-document/token`);
